import qs from 'qs';
import config from '../config';
import {
  MaterialReviewType,
  NotificationActionCategory,
  NotificationActionType,
  NotificationEventType,
  NotificationType,
  OfficeGroupName,
  Pagination,
  PromptType,
  RoarContainerType,
  SearchCategory,
  SearchModalActionDataOutput,
  SearchRoarItemActionType,
  Session,
} from '../openapi/leo';
import { AddressCountryEnum } from '../openapi/yenta';
import { downloadFile } from './FileUtils';
import { getAuthCookie } from './AuthUtils';

export interface ContextualPrompt {
  prompt: string;
  title: string;
  category: string | null | undefined;
  prompt_type?: PromptType;
}

export interface CallSupportDetails {
  container_id?: string | null;
  container_type?: RoarContainerType;
  agent_id: string;
  office_group_id?: string;
  target_type?: string;
  office_group_name?: OfficeGroupName;
}

export interface FollowUpItem {
  id: string;
}

export interface FollowUp {
  type: string;
  items: FollowUpItem[];
}

export interface LeoNotificationResponse {
  items: LeoNotification[];
  pagination: Pagination;
}

export interface LeoNotificationAction {
  id: string;
  text: string;
  type: NotificationActionType;
  url?: string | null;
  category: NotificationActionCategory;
  prompt?: string;
}

export interface LeoNotification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  priority: number;
  actions: LeoNotificationAction[];
  user_response: null;
  suggested_prompts: any[];
  created_at: string;
  dismissed_at: null;
  menu_items: {
    type: NotificationEventType; // 'DEFER', 'MUTE'
    text: string;
    icon: string; // 'defer'
  }[];
  is_recent: boolean;
}

export interface TransactionItem {
  id?: string | null;
  type?: string | null;
  address?: string | null;
  country?: AddressCountryEnum | null;
  price?: string | null;
  escrow_date?: string | null | null;
  listingExpirationDate?: string | null | null;
  commission?: string | null | null;
  text?: string | null;
  lifecycleState?: {
    description?: string | null;
    normalized_state?: string | null;
    state?: string | null;
  } | null;
  externalLifecycleState?: {} | null;
  isNew?: boolean | null;
  isSelected?: boolean | null;
}

export interface LeoResponseMessageContextDetails {
  category?: string | null;
  type?: string | null;
  message?: string | null;
  items?: TransactionItem[] | null;
  prompts?: {
    single_selection?: (string | null | undefined)[] | null;
    multi_selection?: (string | null | undefined)[] | null;
  } | null;
}

export interface CourseItem {
  id?: string | null;
  title?: string | null;
  content?: string | null;
  url?: string | null;
  summary?: string | null;
  chunks?: any[];
}

export enum UserIntentEnum {
  CREATE_TICKET = 'CREATE_TICKET',
  CALL_SUPPORT = 'CALL_SUPPORT',
  SUPPORT_SELECTED = 'SUPPORT_SELECTED',
  SUPPORT_DISMISSED = 'SUPPORT_DISMISSED',
}

export interface LeoResponseMessageContext {
  bot_message_id: string;
  completion: string;
  details: LeoResponseMessageContextDetails[];
  references: {
    courses: CourseItem[];
    documents: {
      id: string;
      title: string;
      content: string;
      url: string;
      summary?: string | null;
      chunk: {
        id: string;
        title?: string;
        content: string;
      };
      q_tags: string[];
    }[];
  };
  media: LeoMediaItem[] | null;
  note: string | null;
  create_ticket: boolean | null;
  support_flow_details: {
    ticket?: {
      can_create: boolean;
      is_created: boolean;
      ticket_url: string | null;
    } | null;
    call?: { can_initiate: boolean; roar_items: CallSupportDetails[] } | null;
  } | null;
  user_intent_type: UserIntentEnum | undefined;
}

export interface SessionWithDisclaimer extends Session {
  id: string;
  disclaimer?: string;
}

export const getLeoResponseStreamUrl = (
  question: string,
  sessionId: string,
  followUps?: FollowUp[],
  helperPrompt?: ContextualPrompt,
  source?: string,
  supportIntent?: UserIntentEnum,
  notificationId?: string,
) => {
  const params: { [key: string]: any } = {
    question,
    session_id: sessionId,
  };
  if (followUps && followUps?.length > 0) {
    params.follow_ups = JSON.stringify(followUps);
  }
  if (helperPrompt) {
    params.helper_prompt = JSON.stringify({
      title: helperPrompt.title,
      prompt: helperPrompt.prompt,
      category: helperPrompt.category,
      prompt_type: helperPrompt.prompt_type,
    });
  }
  if (source) {
    params.source = source;
  }
  if (supportIntent) {
    params.support_intent = supportIntent;
  }
  if (notificationId) {
    params.notification_id = notificationId;
  }

  const url = `${config.apiBasePath.leo}/chat/generate_stream?${qs.stringify(
    params,
  )}`;
  return url;
};

export const getLeoSupportResponseSteamUrl = (
  supportIntent: UserIntentEnum,
  sessionId: string,
  attachmentIds?: string[],
) => {
  const params: { [key: string]: any } = {
    question: null,
    session_id: sessionId,
    support_intent: supportIntent,
    attachment_file_ids: attachmentIds,
  };

  const url = `${config.apiBasePath.leo}/chat/generate_stream?${qs.stringify(
    params,
  )}`;

  return url;
};

export const START_TOKEN = '[START]';
export const DONE_TOKEN = '[DONE]';

export const DEFAULT_CHAT_PROMPTS: ContextualPrompt[] = [
  {
    prompt: 'What can Leo do?',
    title: 'What can Leo do?',
    category: 'none',
  },
  {
    prompt: 'When does my license expire?',
    title: 'When does my license expire?',
    category: 'none',
  },
  {
    prompt: 'What is my open GCI?',
    title: 'What is my open GCI?',
    category: 'none',
  },
  {
    prompt: 'What’s the status of my last transaction?',
    title: 'What’s the status of my last transaction?',
    category: 'none',
  },
  {
    prompt: 'How many people in my network?',
    title: 'How many people in my network?',
    category: 'none',
  },
];

export interface ChatFormData {
  askLeoText?: string;
}

export const MULTILINE_INPUT_MIN_ROWS = 1;
export const MULTILINE_INPUT_MAX_ROWS = 3;

export interface NeoLeoSearchResultItem {
  title?: string;
  url?: string;
  prompt?: string;
  prompt_type?: PromptType;
  interaction?: {
    type: 'CHAT' | 'REDIRECT' | 'ROAR' | 'MODAL';
    target_type?: string;
    action_type?: 'CALL'; // 'CALL'
    title?: string;
    office_group_id?: string;
    office_group_name?: OfficeGroupName;
    container_id?: null;
    container_type?: string;
    agent_id?: string;
    data: SearchModalActionDataOutput;
    form_id: MaterialReviewType;
    request_params?: {
      support_intent: string;
      question: string;
      helper_prompt: {
        title: string;
        prompt: string;
        prompt_type: PromptType;
      };
    };
    url?: 'https://support.therealbrokerage.com/hc/en-us/articles/29935844315799-Real-Wallet-Business-Checking-Account-Frequently-Asked-Questions';
  };
  action_type?: SearchRoarItemActionType;
  office_group_id?: string;
  office_group_name?: OfficeGroupName;
  target_type?: string;
  container_id?: string | null;
  container_type?: RoarContainerType;
  agent_id: string;
}

export interface NeoLeoSearchResult {
  category: SearchCategory;
  category_title?: string | null;
  items: Array<NeoLeoSearchResultItem>;
}

export enum SenderReceiverEnum {
  SYSTEM = 'system',
  USER = 'user',
}

export const LEO_HYPER_PROMPT_LINK_PATTERN = 'leo://action/prompt';

export const DEFAULT_SHOWN_ITEMS_COUNT = 3;
export const DEFAULT_SHOWN_PROPERTY_POSTERS_COUNT = 4;
export const DEFAULT_SHOWN_PROPERTY_POSTERS_COUNT_ON_MOBILE = 4;

export interface LeoShareablesConfig {
  msgId?: string;
  activeShareableIndex?: number;
  showShareablesModal?: boolean;
}

export enum VoteType {
  Upvote = 'upvote',
  Downvote = 'downvote',
}

export enum MediaTypeEnum {
  PROPERTY_LISTING_FLYER = 'PROPERTY_LISTING_FLYER',
  WIDEN_POSTER = 'WIDEN_POSTER',
  SHARRAN = 'SHARRAN',
  DEFAULT = 'DEFAULT',
}

export const getDownloadableImageUrl = (link: string) => {
  const imageDomain = `${config.apiBasePath.leo}/download/image/`;
  const encodedImageSrc = encodeURIComponent(link);
  return `${imageDomain}${encodedImageSrc}`;
};

export const handleLeoListingPosterDownload = async (link: string) => {
  const posterLink = getDownloadableImageUrl(link);
  await downloadFile(posterLink, 'listing-poster.png', {
    headers: new Headers({
      Authorization: `Bearer ${getAuthCookie()}`,
    }),
  });
};

export type UntrustWorthy = string | null | undefined;

export type LeoMediaItem = {
  title?: UntrustWorthy;
  sub_title?: UntrustWorthy;
  snippet?: UntrustWorthy;
  template_url?: UntrustWorthy;
  mark_down?: UntrustWorthy;
  type: MediaTypeEnum;
  source_url: UntrustWorthy;
  media_url: string;
  thumbnail: UntrustWorthy;
  mimetype: UntrustWorthy;
  height: null;
  width: null;
  details: any;
};
